import React, { useEffect, useState } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import axios from "axios";
import {
	Alert,
	Avatar,
	Button,
	Listbox,
	ListboxItem,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	Tab,
	Tabs,
	CircularProgress,
} from "@heroui/react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";

export const FansChart = function FansChart(props) {
	var color = "success";
	const [data, setData] = useState([]);
	const [yDomain, setYDomain] = useState(["auto", "auto"]);

	useEffect(() => {
		if (props.data.length > 0) {
			var cpy = [];
			props.data.forEach((d, i) => {
				cpy.push(d);
				cpy[i].Time = new Date(d.CreatedAt).toLocaleDateString();
			});
			setData(cpy);
			const fansValues = cpy.map((d) => d.Fans);
			const minFans = Math.min(...fansValues);
			const maxFans = Math.max(...fansValues);
			const range = maxFans - minFans;

			let padding;
			if (range === 0) {
				padding = minFans * 0.01;
			} else if (range / minFans < 0.01) {
				padding = range * 2;
			} else {
				padding = range * 0.1;
			}

			setYDomain([minFans - padding, maxFans + padding]);
		}
	}, [props.data]);
	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className=" p-2 border border-gray-200 rounded shadow">
					<p className="text-sm">{`Date: ${label}`}</p>
					<p className="text-sm font-semibold">{`Value: ${payload[0].value.toLocaleString()}`}</p>
				</div>
			);
		}
		return null;
	};
	const formatYAxis = (value) => {
		if (value >= 1000000) {
			return `${(value / 1000000).toFixed(1)}M`;
		} else if (value >= 1000) {
			return `${(value / 1000).toFixed(1)}K`;
		}
		return value.toLocaleString();
	};
	return (
		<ResponsiveContainer className={"h-full"}>
			<AreaChart
				accessibilityLayer
				data={data}

				height={300}
				margin={{
					left: 10,
					right: 10,
					top: 10,
					bottom: 10,
				}}
			>
				<defs>
					<linearGradient
						id="colorGradient"
						x1="0"
						x2="0"
						y1="0"
						y2="1"
					>
						<stop
							offset="10%"
							stopColor={`hsl(var(--heroui-${color}-500))`}
							stopOpacity={0.3}
						/>
						<stop
							offset="100%"
							stopColor={`hsl(var(--heroui-${color}-100))`}
							stopOpacity={0.1}
						/>
					</linearGradient>
				</defs>
				<CartesianGrid
					stroke="hsl(var(--heroui-default-200))"
					strokeDasharray="3 3"
					vertical={false}
				/>
				<XAxis
					axisLine={false}
					dataKey="Time"
					style={{ fontSize: "var(--heroui-font-size-tiny)" }}
					tickLine={false}
					tickMargin={5}
				/>
				<YAxis
					domain={yDomain}
					axisLine={false}
					tickLine={false}
					tickMargin={8}
					tickFormatter={formatYAxis}
					tickCount={6}
					style={{ fontSize: "var(--heroui-font-size-tiny)" }}
				/>
				<Tooltip content={<CustomTooltip />} />
				<Area
					type="monotone"
					dataKey="Fans"
					stroke={`hsl(var(--heroui-${color}))`}
					strokeWidth={2}
					fill="url(#colorGradient)"
					animationDuration={1000}
					animationEasing="ease"
					activeDot={{
						stroke: `hsl(var(--heroui-${color}))`,
						strokeWidth: 2,
						fill: "hsl(var(--heroui-background))",
						r: 5,
					}}
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
};

export const GuardChart = function GuardChart(props) {
	var color = "success";
	const [data, setData] = useState([]);
	const [yDomain, setYDomain] = useState(["auto", "auto"]);

	useEffect(() => {
		if (props.data.length > 0) {
			var cpy = [];
			props.data.forEach((d, i) => {
				cpy.push(d);
				cpy[i].Time = new Date(d.UpdatedAt).toLocaleDateString();
			});
			setData(cpy);
		}
	}, [props.data]);
	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className=" p-2 border border-gray-200 rounded shadow">
					<p className="text-sm">{`Date: ${label}`}</p>
					<p className="text-sm font-semibold">{`Value: ${payload[0].value.toLocaleString()}`}</p>
				</div>
			);
		}
		return null;
	};
	return (
		<ResponsiveContainer className={"h-full"}>
			<AreaChart
				accessibilityLayer
				data={data}
				margin={{
					left: 10,
					right: 10,
					top: 10,
					bottom: 10,
				}}
				onClick={(e, payload) => {
					console.log(e);
					console.log(payload);
					props.onClick(parseInt(e.activeIndex));
				}}
			>
				<defs>
					<linearGradient
						id="colorGradient"
						x1="0"
						x2="0"
						y1="0"
						y2="1"
					>
						<stop
							offset="10%"
							stopColor={`hsl(var(--heroui-${color}-500))`}
							stopOpacity={0.3}
						/>
						<stop
							offset="100%"
							stopColor={`hsl(var(--heroui-${color}-100))`}
							stopOpacity={0.1}
						/>
					</linearGradient>
				</defs>
				<CartesianGrid
					stroke="hsl(var(--heroui-default-200))"
					strokeDasharray="3 3"
					vertical={false}
				/>
				<XAxis
					axisLine={false}
					dataKey="Time"
					style={{ fontSize: "var(--heroui-font-size-tiny)" }}
					tickLine={false}
					tickMargin={5}
				/>
				<YAxis
					domain={yDomain}
					axisLine={false}
					tickLine={false}
					tickMargin={8}
					tickFormatter={(value) => Math.round(value).toString()}
					tickCount={6}
					style={{ fontSize: "var(--heroui-font-size-tiny)" }}
				/>
				<Tooltip content={<CustomTooltip />} />
				<Area
					type="monotone"
					dataKey="Guard"
					stroke={`hsl(var(--heroui-${color}))`}
					strokeWidth={2}
					fill="url(#colorGradient)"
					animationDuration={1000}
					animationEasing="ease"
					activeDot={{
						stroke: `hsl(var(--heroui-${color}))`,
						strokeWidth: 2,
						fill: "hsl(var(--heroui-background))",
						r: 5,
					}}
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
};

export const LiveMessageChart = function LiveMessageChart(props) {
	var color = "success";
	const [data, setData] = useState(props.data || []);
	const [yDomain, setYDomain] = useState(["auto", "auto"]);
	const [hotWords, setHotWords] = useState([]);

	const updateDomain = (list) => {
		if (!list || list.length === 0) return;
		const values = list.map((d) => d.RecordCount ?? d.Count ?? 0);
		const minVal = Math.min(...values);
		const maxVal = Math.max(...values);
		const range = maxVal - minVal;

		let padding;
		if (range === 0) {
			padding = minVal === 0 ? 1 : minVal * 0.1;
		} else if (minVal > 0 && range / minVal < 0.01) {
			padding = range * 2;
		} else {
			padding = range * 0.1;
		}

		setYDomain([
			Math.max(0, Math.floor(minVal - padding)),
			Math.ceil(maxVal + padding),
		]);
	};

	useEffect(() => {
		if (props.data && props.data.length > 0) {
			setData(props.data);
			updateDomain(props.data);
		}
	}, [props.data]);

	useEffect(() => {
		if (
			props.id &&
			props.isOpen &&
			(!props.data || props.data.length === 0)
		) {
			axios.get("/api/live/chart/msg?id=" + props.id).then((res) => {
				if (!res.data.data) {
					props.onClose && props.onClose();
					return;
				}
				setData(res.data.data);
				updateDomain(res.data.data);
			});
		}
		if (props.id && props.isOpen) {
			axios.get("/api/live/hot_word?id=" + props.id).then((res) => {
				setHotWords(res.data.items || []);
			});
		}
	}, [props.id, props.isOpen]);

	useEffect(() => {
		console.log(data);
	}, [data]);

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className=" p-2 border border-gray-200 rounded shadow">
					<p className="text-sm">{`Date: ${label}`}</p>
					<p className="text-sm font-semibold">{`Value: ${payload[0].value.toLocaleString()}`}</p>
				</div>
			);
		}
		return null;
	};

	const [allowClick, setAllowClick] = React.useState(true);

	function formatLlmMarkdown(rawText) {
		if (!rawText) return "";

		let formattedText = rawText;
		formattedText = formattedText.replace(
			/([^\n])\s*(###\s+\d+\.)/g,
			"$1\n\n$2",
		);
		formattedText = formattedText.replace(
			/([\u4e00-\u9fa5]+)\s*\*\s+/g,
			"$1\n* ",
		);
		return formattedText;
	}

	async function fetchStream() {
		var url = "/api/summary/live?id=" + props.id;
		if (import.meta.env.PROD) {
			url = "https://api.vtb.cat/summary/live?id=" + props.id;
		}
		const response = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
		});
		const reader = response.body.getReader();
		const decoder = new TextDecoder("utf-8", { stream: true });

		let buffer = "";
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split(/\r?\n/);
			buffer = lines.pop() || "";

			for (const line of lines) {
				if (!line.trim()) continue;

				const match = line.match(/^data:\s?(.*)$/);
				if (match) {
					const text0 = match[1];

					if (text0 === "[DONE]") {
						console.log("流结束", text);
						break;
					}

					try {
						const parsedData = JSON.parse(text0);
						const content = parsedData.content || "";

						setText((prev) => prev + content);
						setAllowClick(true);
					} catch (e) {
						console.error("解析失败的内容:", text0);
					}
				}
			}
		}
	}

	const [text, setText] = React.useState("");

	const [size, setSize] = useState("3xl");

	return (
		<Modal
			isOpen={props.isOpen}
			size={size}
			onClose={props.onClose}
			scrollBehavior={"inside"}
		>
			<ModalContent>
				<ModalHeader>
					<h2>弹幕</h2>
				</ModalHeader>
				<ModalBody>
					<div className="transition-all duration-1000 ease-in-out">
						<Tabs
							destroyInactiveTabPane={false}
							className="h-full "
							onSelectionChange={(e) => {
								if (e === "cnt") {
									setSize("xl");
								} else {
									setSize("3xl");
								}
							}}
						>
							<Tab key={"chart"} title={"弹幕趋势"}>
								<div style={{ width: "100%", height: 450 }}>
									<ResponsiveContainer
										width="100%"
										height="100%"
									>
										<AreaChart
											accessibilityLayer
											data={data}
											margin={{
												left: 10,
												right: 10,
												top: 10,
												bottom: 10,
											}}
										>
											<defs>
												<linearGradient
													id="colorGradient"
													x1="0"
													x2="0"
													y1="0"
													y2="1"
												>
													<stop
														offset="10%"
														stopColor={`hsl(var(--heroui-${color}-500))`}
														stopOpacity={0.3}
													/>
													<stop
														offset="100%"
														stopColor={`hsl(var(--heroui-${color}-100))`}
														stopOpacity={0.1}
													/>
												</linearGradient>
											</defs>
											<CartesianGrid
												stroke="hsl(var(--heroui-default-200))"
												strokeDasharray="3 3"
												vertical={false}
											/>
											<XAxis
												axisLine={false}
												dataKey="MinuteTime"
												style={{
													fontSize:
														"var(--heroui-font-size-tiny)",
												}}
												tickLine={false}
												tickMargin={5}
												tickFormatter={(value) =>
													new Date(
														value,
													).toLocaleTimeString()
												}
											/>
											<YAxis
												domain={yDomain}
												axisLine={false}
												tickLine={false}
												tickMargin={8}
												tickFormatter={(value) =>
													Math.round(value).toString()
												}
												tickCount={6}
												style={{
													fontSize:
														"var(--heroui-font-size-tiny)",
												}}
											/>
											<Tooltip
												content={<CustomTooltip />}
											/>
											<Area
												type="monotone"
												dataKey="RecordCount"
												stroke={`hsl(var(--heroui-${color}))`}
												strokeWidth={2}
												fill="url(#colorGradient)"
												animationDuration={1000}
												animationEasing="ease"
												activeDot={{
													stroke: `hsl(var(--heroui-${color}))`,
													strokeWidth: 2,
													fill: "hsl(var(--heroui-background))",
													r: 5,
												}}
											/>
										</AreaChart>
									</ResponsiveContainer>
								</div>
							</Tab>
							{props.live.Message < 2 && (
								<Tab key={"summary"} title={"Summary"}>
									<div className="[&>ul]:list-disc [&>ul]:pl-5 [&>ul>li]:mb-1 [&>p]:mb-3">
										<Markdown
											remarkPlugins={[
												remarkBreaks,
												remarkMath,
											]}
										>
											{text}
										</Markdown>
									</div>
									{!allowClick && (
										<CircularProgress aria-label="Loading..." />
									)}
									{allowClick && (
										<Button
											onClick={() => {
												setAllowClick(false);
												setText("");
												fetchStream();
											}}
										>
											Generate Summary
										</Button>
									)}

									<Alert type="info" className={"mt-1"}>
										AI总结。需要十秒钟左右才会输出结果，有些时候可能不可用。背后是Gemini-3.5-flash。现在是半成品，以后也许可以再优化下提示词，然后再把直播回放的语音转录也输入进去。
									</Alert>
								</Tab>
							)}
							<Tab key={"cnt"} title={"弹幕排行"}>
								<Listbox>
									<ListboxItem>
										<div
											className={"flex flex-row w-full"}
											onClick={() => {}}
										>
											<Avatar src={AVATAR_API + "0"} />
											<div
												className={
													"ml-1.5   flex-1 flex justify-between"
												}
											>
												<span className={"font-bold"}>
													Total
												</span>
												<span className={" font-bold "}>
													{props.live.Message}
												</span>
											</div>
										</div>
									</ListboxItem>
									{props.live.MsgRank &&
										props.live.MsgRank.map((i) => {
											return (
												<ListboxItem>
													<div
														className={
															"flex flex-row w-full"
														}
														onClick={() => {
															window.open(
																"/user/" +
																	i.from_id,
															);
														}}
													>
														<Avatar
															src={
																AVATAR_API +
																i.from_id
															}
														/>
														<div
															className={
																"ml-1.5   flex-1 flex justify-between"
															}
														>
															<span
																className={
																	"font-bold"
																}
															>
																{i.from_name}
															</span>
															<span
																className={
																	" font-bold "
																}
															>
																{i.cnt}
															</span>
														</div>
													</div>
												</ListboxItem>
											);
										})}
								</Listbox>
							</Tab>
							<Tab key={"hot"} title={"高频弹幕"}>
								<Listbox>
									{hotWords.map((item) => {
										return (
											<ListboxItem>
												<div
													className={
														"flex flex-row w-full"
													}
													onClick={() => {}}
												>
													<div
														className={
															"ml-1.5   flex-1 flex justify-between"
														}
													>
														<span
															className={
																"font-bold"
															}
														>
															{item.sentence}
														</span>
														<span
															className={
																" font-bold "
															}
														>
															{item.cnt}
														</span>
													</div>
												</div>
											</ListboxItem>
										);
									})}
								</Listbox>
							</Tab>
						</Tabs>
					</div>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
